// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Text } from 'informed';
import moment from 'moment';

import {
  addUpdate,
  deleteUpdate,
  updateUpdate,
  setUpdateArchived,
} from '../../../actions/updateActions';

import { Button, PrimaryButton } from '../../Buttons.jsx';
import DateText from '../../DateText.jsx';
import Table from '../../Table.jsx';
import { Error } from '../../Typography.jsx';

import { findAllUpdates, getUpdateLink, UPDATE_TYPES } from '../../../util/updatesUtil';

type Props = {
  patient: Object,
  updates: Object[],
  addUpdate: Function,
  deleteUpdate: Function,
  updateUpdate: Function,
  setUpdateArchived: Function,
  archivedTable: boolean,
}

type Row = {
  _id: string,  // The id of the update.
  task: string,
  description: string,
  dateDue: string,  // ISO string
  dateCompleted: string,
  type: string,
}

// This component represents the table of updates, located within the container
// updates page for a specific patient.
class UpdatesComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.dateDue = moment(formValues.dateDue, 'DD-MM-YYYY').toISOString();
    formValues.dateCompleted = moment(formValues.dateCompleted, 'DD-MM-YYYY')
      .toISOString();
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteUpdate(this.props.patient.activePatientId, id);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    this.props.updateUpdate(this.props.patient.activePatientId, {
      updateId: id,
      ...formData.values,
    });
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addUpdate(this.props.patient.activePatientId, formData.values);
  }

  // Set archive to true to archive, false otherwise.
  handleArchive = (id: string, archive: boolean) => () => {
    this.props.setUpdateArchived(this.props.patient.activePatientId, id,
      archive);
  }

  // Custom action to archive/unarchive updates.
  actionsColumn = (row: Object) => {
    const link = getUpdateLink(this.props.patient._id, row.type);
    return row.type === UPDATE_TYPES.CUSTOM
      ? (
        <div>
          <Button onClick={this.handleArchive(row._id, !this.props.archivedTable)}>
            {this.props.archivedTable ? 'Unarchive' : 'Archive'}
          </Button>
        </div>
      )
      : (
        <div>
          <Link to={link}>
            <PrimaryButton>
              Link to page
            </PrimaryButton>
          </Link>
        </div>
      );
  }

  render() {
    const columns = [
      {
        Header: 'Task',
        id: 'task',
        editCell: item => <Text field='task' initialValue={item.task} size={64} />,
      },
      {
        Header: 'Description',
        id: 'description',
        editCell: item => <Text field='description' initialValue={item.description} size={64} />,
      },
      {
        Header: 'Due Date',
        id: 'dateDue',
        cell: (item) => {
          if (!item.dateDue) {
            return '--';
          }
          return moment(item.dateDue) < moment() && !item.dateCompleted
            ? <Error>{moment(item.dateDue).format()} (overdue)</Error>
            : moment(item.dateDue).format();
        },
        editCell: item => <DateText field='dateDue' item={item} />,
      },
      {
        Header: 'Date Completed',
        id: 'dateCompleted',
        cell: item => (item.dateCompleted ? moment(item.dateCompleted).format() : '--'),
        editCell: item => <DateText field='dateCompleted' item={item} initEmpty />,
      },
    ];

    // Filter archived updates into appropriate table
    const formattedCustomUpdates = this.props.updates.filter(update => (
      this.props.archivedTable ? update.archived : !update.archived))
      .map(update => ({ type: UPDATE_TYPES.CUSTOM, ...update }));

    // Auto updates need the Update object fields to be mapped to the columns
    // in this table.
    const autoUpdates = [];
    if (!this.props.archivedTable) {
      findAllUpdates(autoUpdates, this.props.patient, 30, false);
    }
    const formattedAutoUpdates = autoUpdates.map(update => ({
      _id: update._id,
      task: update.update,
      description: '--',
      dateDue: moment(update.due, 'DD-MM-YYYY').toISOString(),
      dateCompleted: '',
      type: update.type,
    }));

    const allUpdates: Row[] = formattedCustomUpdates.concat(formattedAutoUpdates);

    return (
      <Table
        columns={columns}
        data={allUpdates}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
        handleSaveNewRow={!this.props.archivedTable && this.handleSaveNewRow}
        extraActions={this.actionsColumn}
        showEdit={row => row.type === UPDATE_TYPES.CUSTOM}
        showDelete={row => row.type === UPDATE_TYPES.CUSTOM}
        omitNotes
        sorted={[
          {
            id: 'dateDue',
            desc: this.props.archivedTable,
          },
        ]}
      />
    );
  }
}

const mapStateToProps = state => ({
  patient: state.patient.data,
  updates: state.patient.data.customUpdates,
});

const mapDispatchToProps = dispatch => ({
  addUpdate: (patientId, update) => dispatch(addUpdate(patientId, update)),
  deleteUpdate: (patientId, updateId) => dispatch(deleteUpdate(patientId, updateId)),
  updateUpdate: (patientId, update) => dispatch(updateUpdate(patientId, update)),
  setUpdateArchived: (patientId, updateId, archive) => dispatch(setUpdateArchived(patientId, updateId, archive)),
});

const Updates = connect(
  mapStateToProps,
  mapDispatchToProps,
)(UpdatesComponent);

export default Updates;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/updates/Updates.jsx